// 🔐 POST AUTENTICADO COMPARTILHADO — B131/B204 (AI_94)
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// Fonte única do padrão "ID Token Firebase + retry em 401" que
// vivia duplicado em ragClient.ts e analyzeClient.ts (débito B131
// anotado no próprio código; achado A3 da auditoria AI_92).
//
// Semântica:
//   - usuário deslogado / refresh falhou → retorna null
//     (o CALLER decide: ragClient degrada graciosamente,
//      analyzeClient converte em throw — laudo exige auth)
//   - 401 inicial → retry 1x com forceRefresh do token
//     (janela rara: token vivo no SDK do client, morto no server)
//   - !ok após retry → throw com rótulo + detalhe do corpo
//   - sucesso → body parseado (response.json())
//
// CFM 2454/2026 Art. 17: proteção contra acessos não autorizados.
// ─────────────────────────────────────────────────────────────

import { auth } from "./firebase";

// ─────────────────────────────────────────────────────────────
// Helper: obtém ID Token com tratamento defensivo
// ─────────────────────────────────────────────────────────────
export async function getIdTokenSafe(
  rotulo: string,
  forceRefresh = false
): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    console.warn(
      `⚠️ [${rotulo}] auth.currentUser=null — usuário deslogado ou sessão não restaurada`
    );
    return null;
  }
  try {
    return await user.getIdToken(forceRefresh);
  } catch (err) {
    console.error(`❌ [${rotulo}] getIdToken falhou:`, err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// POST autenticado com retry em 401 (refresh forçado)
// rotulo: identifica o cliente nos logs/erros (ex.: "ragClient
// buscarChunksDensos", "analyzeClient").
// ─────────────────────────────────────────────────────────────
export async function postAutenticado<T>(
  endpoint: string,
  body: Record<string, unknown>,
  rotulo: string
): Promise<T | null> {
  const response = await fetchAutenticado(endpoint, body, rotulo);
  if (response === null) return null;
  return response.json();
}

// ─────────────────────────────────────────────────────────────
// B389 (AI_157) — a mesma autenticação/retry, devolvendo a Response
// crua (o caller lê o corpo: json OU fluxo NDJSON). postAutenticado
// é um envelope fino sobre esta função — comportamento inalterado.
// ─────────────────────────────────────────────────────────────
export async function fetchAutenticado(
  endpoint: string,
  body: Record<string, unknown>,
  rotulo: string
): Promise<Response | null> {
  // 1ª tentativa — token cacheado (refresh automático se já expirou)
  let token = await getIdTokenSafe(rotulo, false);
  if (!token) return null;

  let response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  // 2ª tentativa — força refresh em caso de 401
  if (response.status === 401) {
    console.warn(`⚠️ [${rotulo}] 401 inicial — forçando refresh do ID Token`);
    token = await getIdTokenSafe(rotulo, true);
    if (!token) return null;
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  }

  if (!response.ok) {
    let detalhe = "";
    try {
      const errBody = await response.json();
      detalhe = errBody.error || JSON.stringify(errBody);
    } catch {
      detalhe = await response.text();
    }
    throw new Error(`[${rotulo}] HTTP ${response.status}: ${detalhe}`);
  }

  return response;
}
